import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../services/api';
import { usePoints } from '../context/PointsContext';
import ReceiptModal from '../components/ReceiptModal';
import './MyOrders.css';

interface Order {
  _id: string;
  orderReference: string;
  items: Array<{
    name: string;
    price: number;
    quantity: number;
    image: string;
  }>;
  total: number;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  paymentReference?: string;
  canRetryPayment?: boolean;
  createdAt: string;
  trackingNumber?: string;
  confirmedAt?: string;
  deliveredAt?: string;
  pointsEarned?: number;
  isPartPayment?: boolean;
  partPaymentStatus?: 'pending' | 'partial' | 'completed' | 'cancelled';
  remainingBalance?: number;
  totalPaid?: number;
  partPaymentDeposit?: number;
  depositConfirmed?: boolean;
}

// Function to get full image URL
const getFullImageUrl = (imagePath: string): string => {
  if (!imagePath) return '/placeholder.png';
  if (imagePath.startsWith('http')) return imagePath;
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${API_URL.replace('/api', '')}/${cleanPath}`;
};

const MyOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { refreshPoints } = usePoints();
  const [completingPaymentId, setCompletingPaymentId] = useState<string | null>(null);
  const [retryingPaymentId, setRetryingPaymentId] = useState<string | null>(null);
  const [receiptOrder, setReceiptOrder] = useState<Order | null>(null);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/orders/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📦 Orders received:', response.data.orders);
      response.data.orders.forEach((order: Order, index: number) => {
        console.log(`Order ${index}:`, {
          id: order._id,
          isPartPayment: order.isPartPayment,
          partPaymentStatus: order.partPaymentStatus,
          remainingBalance: order.remainingBalance,
          totalPaid: order.totalPaid
        });
      });
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();

    const interval = window.setInterval(() => {
      fetchOrders();
    }, 15000);

    const handleNewNotification = () => {
      fetchOrders();
      refreshPoints();
    };

    window.addEventListener('new-notification', handleNewNotification as EventListener);

    // Add scroll listener for go-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('new-notification', handleNewNotification as EventListener);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetchOrders, refreshPoints]);

  // Handle order cancellation
  const handleCancelOrder = async (orderId: string) => {
    if (!window.confirm('Are you sure you want to cancel this order? Once cancelled, this action cannot be undone.')) {
      return;
    }

    setCancellingOrderId(orderId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/orders/cancel-order`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert(response.data.message);
        await refreshPoints();
        await fetchOrders();
      } else {
        alert(response.data.message || 'Failed to cancel order');
      }
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      alert(error.response?.data?.message || 'Failed to cancel order. Please contact support.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  // Check if cancel button should be enabled
  const canCancelOrder = (order: Order): boolean => {
    if (order.depositConfirmed === true) {
      return false;
    }

    if (order.status === 'delivered' || 
        order.status === 'cancelled' || 
        order.status === 'returned' || 
        order.status === 'refunded') {
      return false;
    }
    
    if (order.paymentStatus === 'paid' || 
        order.status === 'paid' || 
        order.status === 'shipped') {
      return false;
    }
    
    return true;
  };

  // Get points display message based on order status
  const getPointsDisplayMessage = (
    order: Order
  ): {
    message: string;
    type: 'earned' | 'deducted' | 'pending' | 'none'
  } => {
    const paymentStatus = String(
      order.paymentStatus || ''
    ).toLowerCase();

    const orderStatus = String(
      order.status || ''
    ).toLowerCase();

    // ----------------------------------------------------
    // CANCELLED / RETURNED / REFUNDED AFTER PAYMENT
    // ----------------------------------------------------
    if (
      (
        orderStatus === 'cancelled' ||
        orderStatus === 'returned' ||
        orderStatus === 'refunded'
      ) &&
      paymentStatus === 'paid'
    ) {
      if (
        order.pointsEarned &&
        order.pointsEarned > 0
      ) {
        return {
          message:
            `ℹ️ ${order.pointsEarned} reward points were deducted because this order was cancelled after payment.`,
          type: 'deducted'
        };
      }

      return {
        message: 'No points were earned for this order.',
        type: 'none'
      };
    }

    // ----------------------------------------------------
    // CANCELLED BEFORE PAYMENT
    // ----------------------------------------------------
    if (
      orderStatus === 'cancelled' &&
      paymentStatus !== 'paid'
    ) {
      return {
        message:
          'No points were deducted since this order was cancelled before payment was completed.',
        type: 'none'
      };
    }

    // ----------------------------------------------------
    // FAILED PAYMENT
    // ----------------------------------------------------
    if (
      paymentStatus === 'failed'
    ) {
      return {
        message:
          '❌ Payment failed. No reward points were awarded.',
        type: 'none'
      };
    }

    // ----------------------------------------------------
    // PAID
    // ----------------------------------------------------
    if (
      paymentStatus === 'paid' ||
      orderStatus === 'paid'
    ) {
      if (
        order.pointsEarned &&
        order.pointsEarned > 0
      ) {
        return {
          message:
            `⭐ Congratulations! You earned ${order.pointsEarned} reward points from this order!`,
          type: 'earned'
        };
      }

      return {
        message: '',
        type: 'none'
      };
    }

    // ----------------------------------------------------
    // PENDING PAYMENT
    // ----------------------------------------------------
    if (
      paymentStatus === 'pending' ||
      paymentStatus === 'processing'
    ) {
      const potentialPoints =
        calculatePotentialPoints(order.total);

      if (potentialPoints > 0) {
        return {
          message:
            `${potentialPoints} points will be awarded after payment confirmation.`,
          type: 'pending'
        };
      }
    }

    return {
      message: '',
      type: 'none'
    };
  };

  // --------------------------------------------------------
// RETRY FAILED PAYSTACK PAYMENT
// --------------------------------------------------------
const handleRetryPayment = async (order: Order) => {
    if (order.canRetryPayment !== true) {
      alert(
        'This order does not have a failed payment that can be retried.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Retry payment of ₦${Number(order.total || 0).toLocaleString()} for order ${
        order.orderReference || order._id.slice(-8)
      }?`
    );

    if (!confirmed) {
      return;
    }

    setRetryingPaymentId(order._id);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert(
          'Your session has expired. Please log in again.'
        );
        return;
      }

      console.log(
        '🔄 Retrying failed Paystack payment for existing order:',
        order._id
      );

      // IMPORTANT:
      // This initializes payment for the EXISTING order.
      // It does NOT create another order.
      const response = await axios.post(
        `${API_URL}/payments/paystack/initialize`,
        {
          orderId: order._id,
          purpose: 'order'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log(
        '🔄 Retry Paystack initialization response:',
        response.data
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
          response.data?.error ||
          'Unable to initialize payment retry.'
        );
      }

      const authorizationUrl =
        response.data?.authorization_url ||
        response.data?.authorizationUrl ||
        response.data?.checkoutUrl ||
        response.data?.data?.authorization_url ||
        response.data?.data?.authorizationUrl ||
        response.data?.data?.checkoutUrl;

      if (!authorizationUrl) {
        console.error(
          'Retry payment response did not contain checkout URL:',
          response.data
        );

        throw new Error(
          'Paystack checkout URL was not returned by the server.'
        );
      }

      console.log(
        '🔗 Retry Paystack authorization URL:',
        authorizationUrl
      );

      // Open Paystack in a new tab, matching Checkout.tsx behavior.
      const paystackWindow = window.open(
        '',
        '_blank'
      );

      if (
        paystackWindow &&
        !paystackWindow.closed
      ) {
        paystackWindow.location.href =
          authorizationUrl;
      } else {
        window.location.href =
          authorizationUrl;
      }

      // Refresh shortly after initialization so
      // the UI reflects any server-side payment changes.
      window.setTimeout(() => {
        fetchOrders();
      }, 2000);

    } catch (error: any) {
      console.error(
        '❌ Error retrying Paystack payment:',
        error?.response?.data || error
      );

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unable to retry payment. Please try again.';

      alert(errorMessage);
    } finally {
      setRetryingPaymentId(null);
    }
  };

  const handleCompletePayment = async (order: Order) => {
    // A balance payment is allowed only after deposit confirmation.
    if (!order.depositConfirmed) {
      alert(
        'Please wait for admin to confirm your deposit before paying the remaining balance.'
      );
      return;
    }

    const remainingBalance = Number(order.remainingBalance ?? 0);
    const totalPaid = Number(order.totalPaid ?? 0);
    const calculatedRemainingAmount = Number(order.total) - totalPaid;

    const remainingAmount =
      remainingBalance > 0
        ? remainingBalance
        : Math.max(0, calculatedRemainingAmount);

    if (!Number.isFinite(remainingAmount) || remainingAmount <= 0) {
      alert('This order has no remaining balance to pay.');
      return;
    }

    const orderReference =
      order.orderReference || order._id.slice(-8);

    const confirmed = window.confirm(
      `Proceed to Paystack for the remaining balance of ₦${remainingAmount.toLocaleString()} for order ${orderReference}?`
    );

    if (!confirmed) {
      return;
    }

    setCompletingPaymentId(order._id);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('Your session has expired. Please log in again.');
        return;
      }

      // Initialize a Paystack balance payment for the EXISTING order.
      // This does not create another order.
      const response = await axios.post(
        `${API_URL}/payments/paystack/initialize`,
        {
          orderId: order._id,
          purpose: 'balance'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
          response.data?.error ||
          'Unable to initialize balance payment.'
        );
      }

      const authorizationUrl =
        response.data?.authorization_url ||
        response.data?.authorizationUrl ||
        response.data?.checkoutUrl ||
        response.data?.data?.authorization_url ||
        response.data?.data?.authorizationUrl ||
        response.data?.data?.checkoutUrl;

      if (!authorizationUrl) {
        console.error(
          'Paystack initialization response did not contain a checkout URL:',
          response.data
        );

        throw new Error(
          'Paystack checkout URL was not returned by the server.'
        );
      }

      // Redirect the customer to Paystack.
      window.location.href = authorizationUrl;
    } catch (error: any) {
      console.error(
        'Error initializing Paystack balance payment:',
        error
      );

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unable to start balance payment. Please try again.';

      alert(errorMessage);
    } finally {
      setCompletingPaymentId(null);
    }
  };

  // Helper function to calculate potential points based on order total
  const calculatePotentialPoints = (total: number): number => {
    return Math.floor(Number(total || 0) / 1000) * 1;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: '#ff9800',
      paid: '#9c27b0', 
      shipped: '#00bcd4',
      delivered: '#4caf50',
      cancelled: '#f44336',
      returned: '#ff5722',
      refunded: '#607d8b'
    };
    return colors[status] || '#757575';
  }; 

  const getStatusText = (status: string) => {
    const texts: { [key: string]: string } = {
      pending: 'Pending Payment',
      paid: 'Processing Order',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      returned: 'Returned',
      refunded: 'Refunded'
    };
    return texts[status] || status;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="orders-loading">
        <div className="spinner"></div>
        <p>Loading your orders...</p>
      </div>
    ); 
  }

  if (orders.length === 0) {
    return (
      <div className="orders-empty">
        <div className="orders-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>My Orders</h1>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h2>No Orders Yet</h2>
          <p>You haven't placed any orders yet.</p>
          <button onClick={() => navigate('/products')}>Start Shopping</button>
        </div>
      </div>
    );
  }

  const handleTrackOrder = (orderId: string) => {
    navigate(`/track-order/${orderId}`);
  };

  return (
    <div className="my-orders-container">
      {/* Add Back Button Header */}
      <div className="orders-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>My Orders</h1>
      </div>
      
      <div className="orders-list">
        {orders.map((order) => {
          const pointsDisplay = getPointsDisplayMessage(order);
          
          return (
            <div key={order._id} className="order-card">
              <div className="my-order-header">
                <div className="order-info">
                  <p className="order-number">Order #{order.orderReference || order._id.slice(-8)}</p>
                  <p className="order-date">{formatDate(order.createdAt)}</p>
                </div>
                <div className="order-status">
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </span>
                  <button 
                    className="receipt-btn"
                    onClick={() => setReceiptOrder(order)}
                  >
                    Receipt
                  </button>
                </div>
              </div>

              {/* Conditional Points Banner based on order status */}
              {pointsDisplay.type !== 'none' && (
                <div className={`order-points-banner ${pointsDisplay.type}`}>
                  <span className="points-icon">
                    {pointsDisplay.type === 'earned' && '⭐'}
                    {pointsDisplay.type === 'deducted' && '❌'}
                    {pointsDisplay.type === 'pending' && '⏳'}
                  </span>
                  <span>{pointsDisplay.message}</span>
                </div>
              )}

              <div className="order-items">
                {order.items.slice(0, 2).map((item, idx) => (
                  <div key={idx} className="order-item">
                    <img 
                      src={getFullImageUrl(item.image)} 
                      alt={item.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder.png';
                      }}
                    />
                    <div className="item-details">
                      <p className="item-name">{item.name}</p>
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                      <p className="item-price">₦{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                {order.items.length > 2 && (
                  <div className="more-items">+{order.items.length - 2} more items</div>
                )}
              </div>

              <div className="order-footer">
                <div className="order-total">
                  <span>Total Amount:</span>
                  <strong>₦{order.total.toLocaleString()}</strong>
                </div>

                {/* Action Buttons Row */}
                <div className="order-actions">
                  <button 
                    className="track-order-btn"
                    onClick={() => handleTrackOrder(order._id)}
                  >
                    Track
                  </button>
                  
                  <button 
                    className="view-order-details-btn"
                    onClick={() => setSelectedOrder(order)}
                  >
                    View
                  </button>

                  {/* Retry Failed Payment */}
                  {order.canRetryPayment === true && (
                    <button
                      className="complete-payment-btn"
                      onClick={() => handleRetryPayment(order)}
                      disabled={retryingPaymentId === order._id}
                    >
                      {retryingPaymentId === order._id ? (
                        <span className="cancel-spinner"></span>
                      ) : (
                        'Pay Now'
                      )}
                    </button>
                  )}
                  
                  {/* Cancel Order Button */}
                  {canCancelOrder(order) && (
                    <button 
                      className="cancel-order-btn"
                      onClick={() => handleCancelOrder(order._id)}
                      disabled={cancellingOrderId === order._id}
                    >
                      {cancellingOrderId === order._id ? (
                        <span className="cancel-spinner"></span>
                      ) : (
                        'Cancel'
                      )}
                    </button>
                  )}
                </div>

                {/* Complete Payment Button - Moved outside order-actions */}
                {order.isPartPayment === true && 
                order.depositConfirmed === true &&
                order.partPaymentStatus === 'partial' && 
                (order.remainingBalance ?? 0) > 0 && (
                  <button 
                    className="complete-payment-btn"
                    onClick={() => handleCompletePayment(order)}
                    disabled={completingPaymentId === order._id}
                  >
                    {completingPaymentId === order._id ? (
                      <span className="cancel-spinner"></span>
                    ) : (
                      `Pay Balance ₦${(order.remainingBalance ?? 0).toLocaleString()}`
                    )}
                  </button>
                )}

                {/* Part-Payment Info */}
                {order.isPartPayment && (
                <div className="part-payment-info-user">
                  <div className="part-payment-details">
                    <span className="part-payment-label"> 
                      <img src="/money_bag_1_trans.png" alt="Naira Bag" className="naira-bag-icon"/>
                      Deposit:
                    </span>
                    <strong>
                      {order.depositConfirmed ? (
                        <span style={{ color: '#4caf50' }}>✓ Paid ₦{(order.partPaymentDeposit || order.totalPaid || 0).toLocaleString()}</span>
                      ) : (
                        <span style={{ color: '#ff9800' }}>₦{(order.partPaymentDeposit || order.totalPaid || 0).toLocaleString()} - Pending</span>
                      )}
                    </strong>
                  </div>
                  <div className="part-payment-details">
                    <span className="part-payment-label">📊 Balance:</span>
                    <strong style={{ color: (order.remainingBalance || 0) > 0 ? '#ff9800' : '#4caf50' }}>
                      ₦{(order.remainingBalance || 0).toLocaleString()}
                    </strong>
                  </div>
                  <div className="part-payment-details">
                    <span className="part-payment-label">📋 Status:</span>
                    <span className={`part-payment-badge-user ${order.partPaymentStatus}`}>
                      {order.depositConfirmed ? (
                        order.partPaymentStatus === 'completed' ? 'Fully Paid' : 'Deposit Confirmed'
                      ) : (
                        'Awaiting Confirmation'
                      )}
                    </span>
                  </div>
                </div>
              )}
                
                <div className="order-payment">
                  <span className='pay-status'>Payment:</span>
                  <span className={`payment-method ${order.paymentMethod}`}>
                    💳 Card Payment
                  </span>
                  <span className={`payment-status ${order.paymentStatus}`}>
                    {String(order.paymentStatus || '').toLowerCase() === 'paid'
                      ? '✓ Paid'
                      : String(order.paymentStatus || '').toLowerCase() === 'failed'
                        ? '✕ Failed'
                        : String(order.paymentStatus || '').toLowerCase() === 'processing'
                          ? '⏳ Processing'
                          : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Go to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop}>
          ↑
        </button>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="close-btn" onClick={() => setSelectedOrder(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>Order Information</h3>
                <p><strong>Order Number:</strong> {selectedOrder.orderReference || selectedOrder._id}</p>
                <p><strong>Date:</strong> {formatDate(selectedOrder.createdAt)}</p>
                <p><strong>Status:</strong> <span style={{ color: getStatusColor(selectedOrder.status) }}>{getStatusText(selectedOrder.status)}</span></p>
                <p><strong>Payment Method:</strong>💳 Card Payment</p>
                <p>
                  <strong>Payment Status:</strong>{' '}
                  {String(selectedOrder.paymentStatus || '').toLowerCase() === 'paid'
                    ? '✓ Paid'
                    : String(selectedOrder.paymentStatus || '').toLowerCase() === 'failed'
                      ? '✕ Failed'
                      : String(selectedOrder.paymentStatus || '').toLowerCase() === 'processing'
                        ? '⏳ Processing'
                        : 'Pending'}
                </p>
                {selectedOrder.trackingNumber && (
                  <p><strong>Tracking Number:</strong> {selectedOrder.trackingNumber}</p>
                )}
                
                {/* Conditional points display in modal */}
                {selectedOrder.status === 'cancelled' && selectedOrder.pointsEarned && selectedOrder.pointsEarned > 0 ? (
                  <p><strong>Points Status:</strong> ❌ <span style={{ color: '#f44336' }}>{selectedOrder.pointsEarned} points cancelled/deducted</span></p>
                ) : selectedOrder.paymentStatus === 'paid' && selectedOrder.status !== 'cancelled' && selectedOrder.pointsEarned && selectedOrder.pointsEarned > 0 ? (
                  <p><strong>Points Earned:</strong> ⭐ {selectedOrder.pointsEarned}</p>
                ) : String(selectedOrder.paymentStatus || '').toLowerCase() === 'failed' ? (
                  <p>
                    <strong>Points Status:</strong>{' '}
                    <span style={{ color: '#f44336' }}>
                      No points were awarded because payment failed.
                    </span>
                  </p>
                ) : (
                  (() => {
                    const paymentStatus = String(
                      selectedOrder.paymentStatus || ''
                    ).toLowerCase();

                    if (
                      paymentStatus !== 'paid' &&
                      paymentStatus !== 'failed' &&
                      selectedOrder.status !== 'cancelled'
                    ) {
                      const potentialPoints =
                        calculatePotentialPoints(
                          selectedOrder.total
                        );

                      return potentialPoints > 0 ? (
                        <p>
                          <strong>Points Pending:</strong>{' '}
                          {potentialPoints} points
                          {' '}
                          (will be awarded after payment)
                        </p>
                      ) : null;
                    }

                    return null;
                  })()
                )}
              </div>

              <div className="detail-section">
                <h3>Items Ordered</h3>
                <div className="items-list">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="detail-item">
                      <img 
                        src={getFullImageUrl(item.image)} 
                        alt={item.name}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/placeholder.png';
                        }} 
                      />
                      <div className="detail-item-info">
                        <p>Quantity: {item.quantity}</p>
                        <p>Price: ₦{item.price.toLocaleString()}</p>
                      </div>
                      <div className="item-subtotal">
                        ₦{(item.price * item.quantity).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3>Order Summary</h3>
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>₦{selectedOrder.total.toLocaleString()}</span>
                </div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <strong>₦{selectedOrder.total.toLocaleString()}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {receiptOrder && (
        <ReceiptModal
          order={receiptOrder}
          onClose={() => setReceiptOrder(null)}
        />
      )}
    </div>
  );
};

export default MyOrders;
